
/// <reference path="appConfig.d.ts" />

const uniq = require('lodash/uniq');
const groupBy = require('lodash/groupBy');
const get = require('lodash/get');

export const DEFAULT_COLOR_CSS = 'lib/app-colors.css';

let baseURI;

export class AuthenticationMethod {
    constructor(private _authenticationSystem: string,
                private _profileName: string,
                private _index: number) {

    }

    get profileName() {
        return this._profileName;
    }

    get authenticationSystem() {
        return this._authenticationSystem;
    }

    get index() {
        return this._index;
    }

    public isExternal() {
        if (this._authenticationSystem === 'SAML' ||
            this._authenticationSystem === 'PDS' ||
            this._authenticationSystem === 'CAS' ||
            this._authenticationSystem === 'SOCIAL_VIA_ALMA') {
            return 1;
        } else {
            return 0;
        }
    }

    public isSilentLoginSupported() {
        if (this._authenticationSystem === 'SAML' ||
            this._authenticationSystem === 'PDS' ||
            this._authenticationSystem === 'CAS') {
            return true;
        } else {
            return false;
        }
    }
}

export function getAuthenticationMethods(): AuthenticationMethod[] {
    let methods = window.appConfig.authentication.map(
        (v, i, arr) => new AuthenticationMethod(v['authentication-system'], v['profile-name'], i)
    );
    return methods;

}

export function getBriefResultConfiguration() {
    return window.appConfig.tiles.ResultTileInterface;
}

export function getFacetConfiguration() {
    return window.appConfig.tiles.FacetTileInterface;
}

export function getLocationsConfiguration() {
    return window.appConfig.tiles.LocationsTileInterface;
}

export function getMainMenuConfiguration() {
    return window.appConfig.tiles.MainMenuTileInterface;
}

export function getFullViewConfiguration() {
    return window.appConfig.tiles.ResultFullTileInterface;
}

export function getBxEnable() {
    return window.appConfig['bx-enable'];
}


export function getSyndeticUnboundEnable() {
    return window.appConfig['syndeticunbound-enable'];
}

export function getSyndeticUnboundId() {
    return window.appConfig['syndeticunbound-id'];
}

export function getSystemConfiguration() {
    return window.appConfig['system-configuration'];
}

export function getEnableEmailClient() {
    const systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined && getBackendSystem() === 'suprima' ?
        systemConfiguration['enable_email_client'] : false;
}

export function getSendMixPanelOnlyForNde() {
    const systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined && getBackendSystem() === 'suprima' ?
        systemConfiguration['send_mixpanel_only_for_nde'] : true;
}

export function getRemoveDuplicateVirtualBrowseCustomerParameter() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['primoVE_remove_duplicate_records_in_virtual_browse'] : false ;
}

export function getIsSupposedToCalcPhysicalServiceIdForExternalRecord() {
    if(getBackendSystem() !== 'suprima')
        return false;
    const systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ?
        systemConfiguration['primoVE_is_supposed_to_calc_physical_service_id_for_external_record'] : true;
}

export function getRapidILLModeCustomerParameter() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['rapido_SA_rapidill_mode'] : false ;
}

export function getRsHidePickupLocationForDigitalRequests() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['temp_rs_hide_pickupLocation_for_digital_requests'] : false ;
}

export function getRapidoAllowPhysicalRequestForEbookOffer() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['rapido_allow_physical_request_for_eBook_offer'] : false ;
}

export function getRequestDefaultFormat() : string {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['request_default_format'] : "PHYSICAL" ;
}

export function getRequestItEnabled() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['request_it_enabled'] : false ;
}

export function getMyAccountNumberOfRequests() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? parseInt(systemConfiguration['primoVE_my_account_number_of_requests']) : 200;
}

export function getInstitutionCode() {
    return window.appConfig['primo-view'].institution['institution-code'];
}

export function getNewspapersSearchEnabled(): boolean {
    return window.appConfig['primo-view'].institution['newspapers-search'];
}

export function getDisplayNewspapersLink(): boolean {
    return getAttributesMap()['displayNewspapersLink'];
}

export function getNewspaperSearchFilterByAvailability() : boolean {
    return getAttributesMap()['newspaperSearchFilterByAvailability'];
}

export function getRefEntryActive(): boolean {
    return getAttributesMap()['refEntryActive'];
}

export function getDisplayFeaturedNewspapers(): boolean {
    return getAttributesMap()['displayFeaturedNewspapers'];
}

export function getInstitutionId() {
    return window.appConfig['primo-view'].institution['id'];
}

export function getBeaconInstitutionCode() {
    return window.appConfig['beacon']['alternative-institution-code'];
}

export function getFindInDbPerTabs() {
    return window.appConfig['primo-view']['showFindInDbPerTabs'];
}

export function getScopes() {
    function getScopeId(e) {
        return e['scope-id'];
    }

    let scopes = window.appConfig['primo-view'].scopes.map(getScopeId);
    return uniq(scopes);
}

export function getSearchConfiguration() {
    return window.appConfig.tiles.SearchTileInterface;
}

export function getQueryTerms() {
    return window.appConfig.queryTerms;
}

export function getTabToTiles(tab) {
    return window.appConfig['tab-to-tiles'][tab];
}

export function getUIComponents(): IUIComponents {
    return window.appConfig.UIComponents;
}

export function getBaseUrls() {
    return window.appConfig['base-urls'];
}

export function getScopeList() {
    return window.appConfig['primo-view'].scopes;
}

export function getDefaultScope() {
    return (getScopes().indexOf('default_scope') !== -1) ? 'default_scope' : getScopes()[0];
}

export function getResultsBulkSize(state: string) {
    if (window.location.href.includes('/collectionDiscovery') && state === 'exploreMain.collectionDiscovery') //if you are in collection discovery state then bulk size is as follows
        return 20;
    else
        return parseInt(window.appConfig['system-configuration']['FE UI – Scrolling Threshold'] || '10');
}

export function getShowMoreThreshold() {
    return parseInt(window.appConfig['system-configuration']['Show More (replaces scrolling) Threshold'] || '50');
}

export function getAttributesMap() {
    return window.appConfig['primo-view']['attributes-map'];
}

export function isRedirectOnlyForAuthName(profileName) {
    let profile = window.appConfig['authentication'].filter((profile) => profile['profile-name'] && profileName == profile['profile-name']);
    if (profile[0] && profile[0]['redirect-only']) {
        return profile[0]['redirect-only'];
    }
    return "false"
}

export function isSaml(profileName) {
    let profile = window.appConfig['authentication'].filter((profile) => profile['profile-name'] && profileName == profile['profile-name']);
    if (profile[0] && profile[0]['authentication-system']) {
        return profile[0]['authentication-system'] === 'SAML';
    }
    return "false"
}

export function getSessionTimeoutParams() {
    //session timeout is in minutes
    return {
        userSessionTimeout: parseInt((getBackendSystem() !== 'suprima' ? 
            window.appConfig['primo-view']['timeout']['user-ui-timeout'] : window.appConfig['system-configuration']['Session Timeout']), 10),
        guestSessionTimeout: parseInt(window.appConfig['primo-view']['timeout']['guest-ui-timeout'], 10),
        sessionTimeoutURL: getAttributesMap().sessionTimeoutURL,
        warningTime: 120,
    };

}

export function getCaptcha() {
    let captchaConf = {
        isActiveCapcha: window.appConfig['system-configuration']['Activate Captcha [Y/N]'],
        public_key: window.appConfig['system-configuration']['Public Captcha Key']
    }
    return captchaConf;
}
export function getSuspendWatchersActivatedForBrowser() {
    if (getBackendSystem() !== 'suprima') {
        try {
            let mtEntry = window.appConfig['mapping-tables']['View Properties'].filter((row) => row.source1 === window.appConfig.vid && row.source2 === 'suspend_watchers_browser_support')
            let suspendWatchersAllBrowsers = get(mtEntry,'[0].target');
            return (suspendWatchersAllBrowsers === 'false') ? "NONE" : "ALL";
        } catch (err) {
            return "ALL";
        }
    }
    return get(window.appConfig, "['system-configuration']['activate_suspend_watchers_for_browser']", "NONE");
}

export function getResultsNumberToDisableSuspendWatchers(){
    
        try{
            if (getBackendSystem() === 'suprima') {
                return parseInt(get(window.appConfig, "['system-configuration']['disable_suspend_watchers_for_x_results']","20"));
            }
            else{
                let mtEntry = window.appConfig['mapping-tables']['Administration Configuration'].filter((row) => row.source1 === 'disable_suspend_watchers_for_x_results');
                if (mtEntry && mtEntry[0]) {
                    return mtEntry[0].target;
                }
                else{
                    return 25;
                }                
            }            
        }
        catch{
            return 20;
        }
}
export function getVid() {
    return window.appConfig.vid;
}

export function isCDI() {
    return window.location.href.includes('searchCDI=true');
}

export function getTabs() {
    return window.appConfig['primo-view']['available-tabs'];
}

export function getTab(scope) {
    let defaultTab = 'default_tab';
    if (getBackendSystem() === 'suprima') {
        defaultTab = window.appConfig['primo-view'].scopes[0]['tab-id-for-scope-matching'];
    }
    let cur = window.appConfig['primo-view'].scopes.filter(function (obj) {
        return obj['scope-id'] === scope;
    });
    if (cur && cur.length > 0) {
        return cur[0]['tab-id-for-scope-matching'] || defaultTab;
    }
    return defaultTab;
}

export function getCurrentPCKey() {
    if (window.appConfig['mapping-tables'] &&
        window.appConfig['mapping-tables'].primo_central_institutions_unique_ids[0]) {
        return window.appConfig['mapping-tables'].primo_central_institutions_unique_ids[0].source1 || '';
    }
}

export function getCurrentServer() {
    return window.location.host.replace(/\:.*/, '');
}

export function getCurrentVersionNumber() {
    if (this.getBackendSystem() === 'suprima') {
        return window.appConfig['system-configuration'].Alma_Version;
    } else if (window.appConfig['system-configuration'].Primo_Version_Number &&
        window.appConfig['system-configuration'].Primo_HotFix_Number) {
        return window.appConfig['system-configuration'].Primo_Version_Number +
            '.' + window.appConfig['system-configuration'].Primo_HotFix_Number
    } else {
        return '4.0.0.0';
    }
}

export function getBeaconUrl() {
    return window.appConfig['system-configuration'].RUM_URL;
}

export function getSummonOverAlma() {
    return window.appConfig.summon_over_alma;
}

export function getRapidoSAEnabled() {
    return window.appConfig.rapido_sa_enabled;
}

export function getDiscoveryServicesPage() {
    return window.appConfig.discovery_services_page;
}
export function getDiscoveryLocalNoLogin() {
    return window.appConfig.discovery_local_no_login;
}

export function isMixPanelenabled() {
    return window.appConfig.enable_mixpanel && !getSendMixPanelOnlyForNde();
}

export function getDiscoveryWithExternalNoLogin() {
    return window.appConfig.discovery_with_external_no_login;
}

export function getLimitedPatronsInAlmaStarter() {
    return window.appConfig.limited_patrons_in_alma_starter  || false;
}
export function getFieldsWithUseTranslation(){
    return window.appConfig.fieldsWithUseTranslation;
}

export function getSearchWithinJournalConfig() {
    return window.appConfig.searchWithinJournalConfig;
}

export function getBackendSystem() {
    if (get(window, 'appConfig.backend_system')) {
        return window.appConfig.backend_system;
    }
    if (!baseURI) {
        baseURI = getBaseURI();
    }
    return baseURI.indexOf('discovery') > -1 ? 'suprima' : undefined;
}

function getBaseURI() {
    let baseElement = document.getElementsByTagName('base');
    return baseElement[0].getAttribute('href');

}

export function getPrimoShortDateFormat() {
    let confEntries = window.appConfig['mapping-tables']['General Configuration'];
    let shortDate = confEntries.filter(entry => entry.source1 === 'Short_Date_Format');
    return shortDate[0].target;
}

export function getPrimoLongDateFormat() {
    let confEntries = window.appConfig['mapping-tables']['General Configuration'];
    let longDate = confEntries.filter(entry => entry.source1 === 'Long_Date_Format');
    return longDate[0].target;
}

export function getIlsDateFormat() {
    return 'yyyyMMdd';
}

export function getUrlContext() {
    if (this.getBackendSystem() === 'suprima') {
        return 'discovery';
    }
    return 'primo-explore';
}

export function getSearchScopesMap() {
    if (!window.appConfig.searchScopesMap) {
        window.appConfig.searchScopesMap = groupBy(getScopeList(), 'tab');
    }
    return window.appConfig.searchScopesMap;
}

export function updateSearchScopesMap(scopes) {
    window.appConfig['primo-view'].scopes = scopes;
    window.appConfig.searchScopesMap = groupBy(getScopeList(), 'tab');
}

export function getDisableCacheParameters() {
    if (this.getBackendSystem() === 'suprima') {
        return window.appConfig['system-configuration'].disable_cache;
    }
}

export function isHoldingsDisplayInLocationsList() : boolean {
    if (this.getBackendSystem() === 'suprima') {
        let discoveryCustomerSettings = window.appConfig['mapping-tables']['Holdings Display In Locations List'];
        let isHoldingsDisplayInLocationsList = discoveryCustomerSettings.filter(entry => entry.target === 'holdings_fields_display_in_locations_list_view');
        return isHoldingsDisplayInLocationsList[0].source2 != "false";
    }
    return false;
}


/**
 * API base url for current institution.
 * @returns string (empty string means its 'ovl')
 */
export function getApiBaseUrl() {
    let instCode = getInstitutionCode();
    let baseUrlsTable = window.appConfig['mapping-tables']['Institution Base URLs'];
    let baseUrlEntry = baseUrlsTable.filter(entry => entry.source1 === instCode && entry.source2 === 'api_base');
    if (baseUrlEntry.length > 0) {
        return baseUrlEntry[0].target;
    }
    return '';
}

export function getRtlLanguages() {
    return new Set(['iw', 'he', 'ar']);
}

export function getPagingType() {
    return 'infinite_scroll';
    //return 'paging';
}

export function isCitationTrailsEnabled(): boolean {
    return Boolean(getAttributesMap()['citationTrailsEnabled']);
}

export function isCitationTrailsFilterByAvailability(): boolean {
    return Boolean(getAttributesMap()['citationTrailsFilterByAvailability']);
}

export function getCitationLinkerBaseURL(): string {
    return window.appConfig['institution-base-url'];
}

export function getSyndetixWithProxy() {
    return window.appConfig['system-configuration']['Proxy_Server'] + 'syndetics.com/index.php?client=';
}

export function getGoogWithProxy() {
    return window.appConfig['system-configuration']['Proxy_Server'] + 'books.google.com/books?bibkeys=';
}

export function getAutocompleteUrl() {
    return window.appConfig['system-configuration'].Auto_Complete_Server_URL + '/solr/ac';
}

export function getPrimoVeSkipDelivery() {
    return window.appConfig['system-configuration']['skip_delivery'];
}

export function getPrimoVeSkipDeliveryForCollectionDiscovery() {
    return this.getBackendSystem() === 'suprima' && window.appConfig['system-configuration']['skip_delivery_for_collection_discovery'];
}

export function getPrimoVeAlternativeEmailDigitalResourceSharing() {
    return window.appConfig['system-configuration']['manualAlternativeEmailRS'];
}

export function getPrimoSplitFacetsMaxWait() {
    return window.appConfig['system-configuration']['split_facets_max_wait'];
}

export function getPrimoVeSplitFacetsWaitInterval() {
    return window.appConfig['system-configuration']['split_facets_wait_interval'];
}

export function getPublicAccessModelEnabled() {
    return window.appConfig['system-configuration']['activeAccessModelEnabled'];
}

export function getAZLangages() {
    return window.appConfig['mapping-tables']['A-Z Languages'];
}

export function getLocalFieldsToDisplayFromAllDedupMembers() {
    return window.appConfig['mapping-tables']['Local Fields To Display From All Dedup Members'];
}

export function getSEOParamsMapping() {
    return window.appConfig['mapping-tables']['seo_params_mapping'];
}

export function getPrimoVeDeliveryBulkFromBrief() {
    return window.appConfig['system-configuration']['delivery_bulk_from_brief'];
}

export function getPrimoVeBriefResultsJournalCoverage() {
    return window.appConfig['system-configuration']['brief_results_journal_coverage'];
}

export function getSEOParamsMappingParam(param) {
    let seoMappingTable = this.getSEOParamsMapping();
    if (seoMappingTable) {
        let titlePath = seoMappingTable.filter(entry => entry.source1 === param);
        if (titlePath && titlePath[0]) {
            return titlePath[0].target;
        }
    }
    return '';
}


export function isEnableSingleLogout() {
    return window.appConfig['enableSingleLogout'];
}

export function isAutoSearchOnTabChange() {
    return getAttributesMap()['tabsRemote'] === 'true';
}

export function getShowICPLicenseFooter() {
    return window.appConfig['system-configuration']['Show ICP License Footer'];
}


export function getBlendFacetsSeparatelyConfig() {
    if (this.getBackendSystem() === 'suprima') {
        return false; //this option isn't supported currently in the primoVE backend
    }
    return window.appConfig['system-configuration']['blend facets separately'] ? window.appConfig['system-configuration']['blend facets separately'] === 'true' : true;
}

export function getLegantoTemplateUrl(): string {
    return getAttributesMap()['legantoURLTemplate'];
}

export function getMultilingualConfigurationEnabled(): boolean {
    return getAttributesMap()['multilingualConfigurationEnabled'];
}

export function getRequestItemAvailabilityCheckTimeout() {
    return parseInt(get(window, 'appConfig["system-configuration"]["request item availability check timeout"]', '1')) * 60 * 1000;
}

export function getFindItemLevelDescriptionsTimeout() {
    return parseInt(get(window, 'appConfig["system-configuration"]["find item level descriptions timeout"]', '1')) * 60 * 1000;
}

export function isUnionCatalogNZView() {
    if (getBackendSystem() !== 'suprima') {
        return false; //this option isn't supported currently in the primo backend
    }
    let consortiaMembers = get(window.appConfig, "['mapping-tables']['consortia member codes']", []);
    let isUnionView = get(window.appConfig, "['primo-view']['is-union-catalog-view']", false);
    return isUnionView && consortiaMembers.length > 0;
}

export function isUnionCatalogMemberView() {
    if (getBackendSystem() !== 'suprima') {
        return false; //this option isn't supported currently in the primo backend
    }
    let isUnionView = get(window.appConfig, "['primo-view']['is-union-catalog-view']", false);
    return isUnionView && !isUnionCatalogNZView();
}

export function isDisplayUnpaywallLinks() {
    if (getBackendSystem() !== 'suprima') {
        return false; 
    }
    return get(window.appConfig, "['primo-view']['display-unpaywall-links']", false);
}

export function isNgrsSearch(stateParams) {
    if (isNgrs() && !this.isFullView) {
        let scopes = getScopeList();
        let scopeTab = stateParams['tab'];
        let scope = scopes.find(scope => scope['tab'] == scopeTab);
        if(scope && scope['scope-id'] == 'CentralIndex'){
            let availableOnly = window.appConfig['primo-view']['pc-availability-tab-scopes-map'][scopeTab]['CentralIndex'];
            return availableOnly && availableOnly !== 'AVAILABLE_ONLY';
        }
    }
    return false;
}

export function passesNgrsImplementationModeCheck() {
    if (window.appConfig['system-configuration'] && window.appConfig['system-configuration'].ngrs_implementation_mode) {
        if(isRapidoFlagOn()) {
        	return true;
        } else {
        	return false;
        }
    }
    return true;
}

export function passesDisableRapidoParam() {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const disableRapido = urlParams.get('disableRapido');
        return !disableRapido == true;
    } catch (err) {
        //safari incognito mode will throw error here
    }
    return true;
}

export function useRapidoDisplay() {
    if (window.appConfig['system-configuration'] && window.appConfig['system-configuration'].use_rapido_functionality) {
        return true;
    }
    return false;
}

export function allowSelfRegistration() {
    if (window.appConfig['system-configuration'] && window.appConfig['system-configuration'].allow_self_registration) {
        return true;
    }
    return false;
}

export function getSelfregistrationTypes(){
    return get(window.appConfig, "['system-configuration']['self_registration_types']");
}

export function isRapidoFlagOn() {
	try {
		let isRapido = window.sessionStorage.getItem("isRapido");
    	return isRapido === 'true';
    } catch (err) {
    	//safari incognito mode will throw error here
    }
    return false;
}

export function isNgrs() {
     return window.appConfig['system-configuration'] && window.appConfig['system-configuration'].ngrs_enabled
      && passesNgrsImplementationModeCheck() && useRapidoDisplay() && passesDisableRapidoParam();
}

export function isGTISearch() {
     return window.appConfig['system-configuration'] && 
     	((window.appConfig['system-configuration'].ngrs_enabled
      		&& passesNgrsImplementationModeCheck() && useRapidoDisplay()) ||
     			 window.appConfig['system-configuration'].cdi_enable_global_title_catalog);
}

export function getKeepMeSignInConfig() {
    let keepMeSignInConf = {
        isEnableExtendingTheSession: (getBackendSystem() !== 'suprima') ? isKeepMeSignInPrimoParam('enable_extending_the_session'): isKeepMeSignInPrimoVEParam('enableExtendSession'),
        isEnableExtendingTheSessionToMax: (getBackendSystem() !== 'suprima') ? isKeepMeSignInPrimoParam('enable_extending_the_session_to_maximum') : isKeepMeSignInPrimoVEParam('enableExtendSessionToMax'),
        isEnableUserSettingForExtendSession: (getBackendSystem() !== 'suprima') ? isKeepMeSignInPrimoParam('allow_user_extend_session_personal_settings') : isKeepMeSignInPrimoVEParam('enableUserSettingForExtendSession')
    }
    
    return keepMeSignInConf;
}

export function getChosenKeepMeSignInConf(){
    let keepMeSignInConf = getKeepMeSignInConfig();
    let config = null;

    // In case both enable extending the session and enable extending the session to maximum are set to 'true' - 
    // the second parameter (enable extending the_session to maximum) should be taken into account
    if(keepMeSignInConf.isEnableExtendingTheSession){
        config = 'extend';
        if(keepMeSignInConf.isEnableExtendingTheSessionToMax){
            config = 'extend_max';
        }
    }else if(keepMeSignInConf.isEnableExtendingTheSessionToMax){
        config = 'extend_max';
    }
    return config;
}

export function isKeepMeSignInPrimoParam(param){
	let props = window.appConfig['mapping-tables']['Institution Properties'];
	if(!props) return false;
	let prop = props.filter(entry => entry.source1 === param);
    if (prop && prop.length > 0) {
        return prop[0].target.toLowerCase()==='true';
    }
    return false; // The default value should be false (when no entry exists)
}

export function isKeepMeSignInPrimoVEParam(param){
	return window.appConfig[param];
}

export function getCountryCodeList(){
	return window.appConfig['country-codes'];
} 

export function getUnionViewScopeSuffix() {
    return get(window.appConfig, "['system-configuration']['unionViewScopeSuffix']");
}

export function getNumberOfRepresentationsForViewIt(){
    return get(window.appConfig, "['system-configuration']['number_of_representations']");
}

export function getAllowActivityOnTransferredFinesFees(){
    return get(window.appConfig, "['system-configuration']['allow_activity_on_transferred_finesfees']");
}

export function getUseFacetInCreatorHyperlink(){
    return get(window.appConfig, "['system-configuration']['use_facet_in_creator_hyperlink']");
}

export function getEqualsSearchOperatorHypertextLinkingEnabled(){
    return get(window.appConfig, "['system-configuration']['equals_search_operator_hypertext_linking_enabled']");
}

export function getEqualsSearchOperatorHypertextLinkingTitleFieldEnabled(){
    return get(window.appConfig, "['system-configuration']['equals_search_operator_hypertext_linking_enabled_title_field']");
}

export function getEqualsSearchOperatorHypertextLinkingAuthorFieldEnabled(){
    return get(window.appConfig, "['system-configuration']['equals_search_operator_hypertext_linking_enabled_author_field']");
}

export function getEqualsSearchOperatorHypertextLinkingSubjectFieldEnabled(){
    return get(window.appConfig, "['system-configuration']['equals_search_operator_hypertext_linking_enabled_subject_field']");
}
export function getUseEsploroAssetPage() {
    return window.appConfig.useEsploroFullAssetPage;
}

export function getUseEsploroSearchPage() {
    return window.appConfig.useEsploroSearchPage;
}
export function getResultsPerPageForVEEnabled() {
    return get(window.appConfig, "['system-configuration']['tmp_enable_results_per_page']",false);
}

export function getSortSubjectsFullDisplay() {
    return get(window.appConfig, "['system-configuration']['subjects_alphabetical_sort_fullDisplay']",false);
}

export function getVENumberOfResultsPerPageSeries(){
    let SeriesString = get(window.appConfig, "['system-configuration']['number_of_results_per_page_series']");
    try {
        return SeriesString.split(',').map((n) => parseInt(n));
    }
    catch(err){
        return [10,25,50];
    }
}

export function getQuickLinkEnabled() {
    return get(window.appConfig, "['primo-view']['display_quick_links']", false) === 'true';
}

export function isHideHowToGetSection() {
     return window.appConfig['system-configuration'] && window.appConfig['system-configuration'].rapido_hide_how_to_get_it_section;
}

export function getViewItShowAllResults() {
    return get(window.appConfig, "['system-configuration']['view_it_show_all_results']");
}
export function getUseIcelandicOrder() {
    return get(window.appConfig, "['system-configuration']['facet_alphanumeric_icelandic_sort']",false);
}
export function getUseExpandedDBLabel() {
    return get(window.appConfig, "['system-configuration']['use_expanded_db_label']",false);
}

export function getEnableDirectLinkingInFullView() {
    return get(window.appConfig, "['system-configuration']['enable_direct_linking_in_record_full_view']",false);
}

export function getDisplayLocationLevelInGetItSingleLocation() {
    return get(window.appConfig, "['system-configuration']['display_location_level_in_getIt_single_location']",false);
}

export function getDisplaySmsWanted() {
    return get(window.appConfig, "['system-configuration']['display_sms_wanted']",false);
}

export function getAutoSwitchQuicklinks() {
    return get(window.appConfig, "['system-configuration']['auto_switch_quicklinks']",true);
}

export function getFeatureFlags() {
    return get(window.appConfig, "['feature-flags']", []);
}

export function isEnabledAsyncBriefResultDelivery() {
    return get(window.appConfig, "['system-configuration']['async_brief_result_delivery']", false);
}

export function getDisplayHoldingsWithoutWaiting() {
    return get(window.appConfig, "['system-configuration']['display_holdings_without_waiting']", false);
}

export function getAllowStartWithForCallNumber(){
    return get(window.appConfig, "['system-configuration']['allow_start_with_for_call_number']");
}

export function getAlphabeticalSortPickupInstLib(){
    return get(window.appConfig, "['system-configuration']['alphabetical_sort_pickup_inst_lib']");
}

export function getDirectLoginTransferAllParameters() {
    return window.appConfig['system-configuration'] && window.appConfig['system-configuration'].direct_login_transfer_all_parameters;
}

export function isShowPhysicalJournalOffer() {
    return window.appConfig['system-configuration'] && window.appConfig['system-configuration'].rapido_show_physical_journal_offer;
}

export function isRapidoOfferByResourceTypeEnabled() {
    let systemConfiguration = getSystemConfiguration();
    return systemConfiguration !== undefined ? systemConfiguration['rapido_offer_by_resource_type_enabled'] : false;
}

export function getDisplayPersonInfoCard(){
    return get(window.appConfig, "['system-configuration']['display_entity_info_card']");
}

export function getEnablePersonEntityAutocomplete(){
    return get(window.appConfig, "['system-configuration']['enable_entity_autocomplete']");
}

export function isShowReasearcherAssistantByIcon(){
    return get(window.appConfig, "['system-configuration']['show_researcher_assistant_by_icon']");
}

export function isShowReasearcherAssistantByWidget(){
    return get(window.appConfig, "['system-configuration']['show_researcher_assistant_by_widget']");
}

export function getEnableResearchAssistantFor(){
    return get(window.appConfig, "['system-configuration']['enable_research_assistant_for']");
}

export function getSaveUsersRaSearchHistory(){
    return get(window.appConfig, "['system-configuration']['save_users_ra_search_history']");
}

export function getPersonEntityAutocompleteUrl() {
    return get(window.appConfig, "['system-configuration']['person_entity_autocomplete_server_url']");
}

export function getSpectoMaxFileSizeForDisplay() {
    return get(window.appConfig, "['system-configuration']['specto_max_file_size_for_display']");
}

export function getSpectoMaxPdfTifFileSizeForDisplay() {
    return get(window.appConfig, "['system-configuration']['specto_max_pdf_tif_file_size_for_display']");
}

export function getCitationConfiguration() {
    return window.appConfig.citations;
}

export function getAutocompleteRequestPunctuationRegex(){    
        if (getBackendSystem() === 'suprima') {
            return get(window.appConfig, "['system-configuration']['prima_autocomplete_request_punctuation_regex']");
        }else{
            let mtEntry = window.appConfig['mapping-tables']['Administration Configuration'].filter((row) => row.source1 === 'autocomplete_request_punctuation_regex');
            return (mtEntry && mtEntry[0]) ? mtEntry[0].target : '';
        }            
}




// WEBPACK FOOTER //
// ./src/main/webapp/components/appConfig/appConfigForward.ts